import { z } from 'zod'
import { allowlistEntryKindSchema, allowlistEntryStatusSchema } from '../enums/allowlist-entry.enum.js'
import { mailDeliveryStatusSchema } from '../enums/mail-delivery-status.enum.js'
import { uuidSchema } from '../validators/params.js'

/** A captured test-mode email as the mailbox lists it. */
export const capturedMailListItemDtoSchema = z.object({
  id: uuidSchema,
  envelopeId: uuidSchema,
  envelopeTitle: z.string(),
  recipient: z.string(),
  subject: z.string(),
  status: mailDeliveryStatusSchema,
  error: z.string().nullable(),
  createdAt: z.string().datetime(),
})

export type CapturedMailListItemDto = z.infer<typeof capturedMailListItemDtoSchema>

export const capturedMailListDtoSchema = z.object({
  messages: z.array(capturedMailListItemDtoSchema),
})

export type CapturedMailListDto = z.infer<typeof capturedMailListDtoSchema>

/** One captured email, with its rendered body. Its links work (they are test links). */
export const capturedMailDtoSchema = capturedMailListItemDtoSchema.extend({
  signerId: uuidSchema.nullable(),
  answererId: uuidSchema.nullable(),
  html: z.string().nullable(),
  text: z.string(),
})

export type CapturedMailDto = z.infer<typeof capturedMailDtoSchema>

/** A test envelope's captured emails with their bodies, for CI (`GET /v1/envelopes/{id}/emails`). */
export const capturedMailWithBodiesListDtoSchema = z.object({
  messages: z.array(capturedMailDtoSchema),
})

export type CapturedMailWithBodiesListDto = z.infer<typeof capturedMailWithBodiesListDtoSchema>

/** An allowlist entry; domains carry the DNS record that proves them. */
export const allowlistEntryDtoSchema = z.object({
  id: uuidSchema,
  kind: allowlistEntryKindSchema,
  value: z.string(),
  status: allowlistEntryStatusSchema,
  dnsRecord: z.object({ type: z.literal('TXT'), name: z.string(), value: z.string() }).nullable(),
  verifiedAt: z.string().datetime().nullable(),
  /** Domains only: set while the entry is paused because a weekly re-check found its TXT record gone. */
  dnsLapsedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
})

export type AllowlistEntryDto = z.infer<typeof allowlistEntryDtoSchema>

export const allowlistDtoSchema = z.object({
  entries: z.array(allowlistEntryDtoSchema),
})

export type AllowlistDto = z.infer<typeof allowlistDtoSchema>

/** What the confirmation page shows once an address is confirmed. */
export const allowlistConfirmedDtoSchema = z.object({
  email: z.string(),
  tenantName: z.string(),
})

export type AllowlistConfirmedDto = z.infer<typeof allowlistConfirmedDtoSchema>
